<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase, PatchedCustomerCaseRequest } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { CasesService } from '@/types/generated/services.gen'
  import type { GenericObject } from 'vee-validate'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import InputField from '@/components/admin/form/InputField.vue'

  const props = defineProps<{
    caseData: CustomerCase
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['update:payload'])

  const { t } = useTranslation({
    en: {
      label: 'Label',
      labelRequired: 'Label is required',
      caseGroup: 'Case Group',
      caseGroupRequired: 'Case Group is required',
      caseGroupInvalid: 'Case Group must be 0 or greater',
      advoluxSection: 'Advolux Information'
    },
    de: {
      label: 'Bezeichnung',
      labelRequired: 'Bezeichnung ist erforderlich',
      caseGroup: 'Aktenkreis',
      caseGroupRequired: 'Aktenkreis ist erforderlich',
      caseGroupInvalid: 'Aktenkreis muss 0 oder größer sein',
      advoluxSection: 'Advolux Information'
    }
  })

  const defaultLabel = computed(() => {
    const { law, client, opponent } = props.caseData.data

    if (law.field === 'Strafrecht') {
      return `${client.first_name} ${client.last_name}`
    }

    const clientName =
      client.is_company && client.company_name
        ? `${client.company_name} ${client.company_legal_form || 'GmbH'}`
        : client.last_name

    if (!opponent) {
      return `${clientName} ./.`
    }

    const opponentName =
      opponent.is_company && opponent.company_name
        ? `${opponent.company_name} ${opponent.company_legal_form || 'GmbH'}`
        : opponent.last_name

    return `${clientName} ./. ${opponentName}`
  })

  watch(
    () => defaultLabel.value,
    (newValue) => {
      formData.label = newValue
    },
    { deep: true }
  )

  onMounted(() => {
    handleSave({
      label: defaultLabel.value,
      caseGroup: 0
    })
  })

  const schema = {
    label: yup.string().required(t('labelRequired')),
    caseGroup: yup
      .mixed()
      .required(t('caseGroupRequired'))
      .test('is-empty-or-valid-number', t('caseGroupInvalid'), function (value) {
        // Allow NaN
        if (typeof value === 'number' && isNaN(value)) {
          return true
        }
        // Allow numbers greater than -1
        if (typeof value === 'number' && value > -1) {
          return true
        }
        return false // Invalid value
      })
  }

  interface FormData {
    label: string
    caseGroup: number
  }

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors
  } = useFormWithValidation<FormData>(
    {
      label: defaultLabel.value,
      caseGroup: 0
    },
    schema
  )

  defineExpose({
    validate,
    showErrors
  })

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:payload', updatedData)
  }

  watch(
    formData,
    (newValue) => {
      handleSave(newValue)
    },
    { deep: true }
  )
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold">{{ t('advoluxSection') }}</h2>
    <AutoSavingForm
      :debounce-delay="debounceDelay"
      :throttle-interval="throttleInterval"
      class="space-y-6"
      @save="handleSave"
    >
      <div class="space-y-4">
        <div>
          <InputField
            v-model="formData.label"
            :label="t('label')"
            name="label"
            :error="formErrors?.value?.label ?? null"
            :rules="schema.label"
            @update:model-value="(value: any) => handleFieldChange('label', value)"
            @focus="() => handleFieldTouch('label')"
          />
          <InputField
            v-model="formData.caseGroup"
            :label="t('caseGroup')"
            type="number"
            name="caseGroup"
            :error="formErrors?.value?.label ?? null"
            :rules="schema.caseGroup"
            @update:model-value="(value: any) => handleFieldChange('caseGroup', value)"
            @focus="() => handleFieldTouch('caseGroup')"
          />
        </div>
      </div>
    </AutoSavingForm>
  </div>
</template>
